import { useState, useEffect } from 'react';
import { Search, Plus, Save, CheckCircle, Edit2, Trash2, X, Mail } from 'lucide-react';
import DateField from './DateField.jsx';
import { POLICY_TYPES } from '../policyTypes.js';
import { toast } from './Toast.jsx';
import { confirmDialog, Combobox, NoteEditor, RichNote } from './ui.jsx';
import {
  createLead as apiCreateLead,
  updateLead as apiUpdateLead,
  deleteLead as apiDeleteLead,
  convertLeadToClient as apiConvertLead,
  mapPolicyFromApi,
  getAgencySettings,
  updateAgencySettings,
} from '../api.js';
import { formatPhoneUS, fmt } from '../utils.js';

// LOCAL today as 'YYYY-MM-DD'. toISOString() returns the UTC date, which after
// ~8pm in Florida (UTC-4/-5) rolls to tomorrow (feedback_local_day_not_utc).
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Hoisted to module scope so its edit state survives parent re-renders. Nested
// inside LeadsView it was a fresh component identity each render, so any parent
// re-render (e.g. an SSE lead update arriving while the form is open) remounted
// it and wiped the half-typed lead. (feedback_setstate_updater_side_effects)
function LeadForm({ initial, onSave, onCancel, isEdit, lang, assignOptions, emptyLead }) {
    const [f, setF] = useState(initial || emptyLead);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
    const toggleInterest = (pid) => set("policyInterest", (f.policyInterest || []).includes(pid) ? f.policyInterest.filter(x => x !== pid) : [...(f.policyInterest || []), pid]);
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
        <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 520, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>🎯 {isEdit ? (lang === "es" ? "Editar Lead" : "Edit Lead") : (lang === "es" ? "Nuevo Lead" : "New Lead")}</div>
            <button type="button" onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".65rem", marginBottom: ".75rem" }}>
            {[["firstName", lang === "es" ? "Nombre" : "First Name"], ["lastName", lang === "es" ? "Apellido" : "Last Name"]].map(([k, l]) => (
              <div key={k}>
                <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{l}</label>
                <input className="dkt-inp" value={f[k] || ""} onChange={e => set(k, e.target.value)} />
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".65rem", marginBottom: ".75rem" }}>
            {[["phone", lang === "es" ? "Teléfono" : "Phone"], ["email", lang === "es" ? "Correo" : "Email"]].map(([k, l]) => (
              <div key={k}>
                <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{l}</label>
                <input
                  className="dkt-inp"
                  value={f[k] || ""}
                  onChange={e => set(k, e.target.value)}
                  onBlur={k === "phone" ? (e => set("phone", formatPhoneUS(e.target.value))) : undefined}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".65rem", marginBottom: ".75rem" }}>
            <div>
              <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Fuente" : "Source"}</label>
              <select className="dkt-inp" value={f.source} onChange={e => set("source", e.target.value)}>
                {["referral", "facebook", "website", "cold_call", "event", "other"].map(s => <option key={s} value={s}>{s.replace("_", " ").replace(/\b\w/g, c => c.toUpperCase())}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Idioma" : "Language"}</label>
              <select className="dkt-inp" value={f.lang || "spanish"} onChange={e => set("lang", e.target.value)}>
                <option value="spanish">{lang === "es" ? "Español" : "Spanish"}</option>
                <option value="english">{lang === "es" ? "Inglés" : "English"}</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Seguimiento" : "Follow Up"}</label>
              <DateField className="dkt-inp" value={f.followUp || ""} onChange={e => set("followUp", e.target.value)} />
            </div>
          </div>
          <div style={{ marginBottom: ".75rem" }}>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Asignado a" : "Assigned to"}</label>
            <Combobox value={f.assignedLabel || ""}
              onChange={v => set("assignedLabel", v)}
              options={assignOptions}
              placeholder={lang === "es" ? "Escribe un nombre…" : "Type a name…"} lang={lang} />
          </div>
          <div style={{ marginBottom: ".75rem" }}>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 6 }}>{lang === "es" ? "Interés en Pólizas" : "Policy Interest"}</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".35rem" }}>
              {/* Todos los tipos, con los propios de la agencia al final (Lissette
                  2026-10-19). Antes solo los 11 primeros de fábrica. */}
              {POLICY_TYPES.map(pt => {
                const on = (f.policyInterest || []).includes(pt.id);
                return <button type="button" key={pt.id} onClick={() => toggleInterest(pt.id)}
                  style={{ padding: ".25rem .55rem", borderRadius: 7, border: `1px solid ${on ? pt.color + "55" : "var(--c-border,#1a3550)"}`, background: on ? pt.color + "18" : "transparent", color: on ? pt.color : "var(--c-text3,#45688a)", fontSize: ".68rem", fontWeight: on ? 700 : 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, transition: "all .15s", fontFamily: "inherit" }}>
                  <span>{pt.icon}</span>{lang === "es" ? pt.labelEs : pt.label}
                </button>;
              })}
            </div>
          </div>
          <div style={{ marginBottom: ".75rem" }}>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Notas" : "Notes"}</label>
            <NoteEditor lang={lang === "es" ? 'es' : 'en'} rows={3} value={f.notes || ""} onChange={v => set("notes", v)} />
          </div>
          <div style={{ display: "flex", gap: ".5rem", justifyContent: "flex-end" }}>
            <button type="button" onClick={onCancel} className="dkt-btn-s">{lang === "es" ? "Cancelar" : "Cancel"}</button>
            <button type="button" onClick={() => onSave(f)} disabled={!f.firstName || !f.lastName}
              style={{ display: "flex", alignItems: "center", gap: 5, background: (!f.firstName || !f.lastName) ? "#2a4560" : "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1.2rem", borderRadius: 9, cursor: (!f.firstName || !f.lastName) ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              <Save size={13} />{lang === "es" ? "Guardar" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
}

export function LeadsView({ leads, onLeadsChange, clients, onClientsChange, onPoliciesAdd, currentUser, t, lang }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [showAutoReply, setShowAutoReply] = useState(false);
  // Seats can't edit agency-wide settings (the API enforces it too); hide the
  // gear for them so they don't hit a 403.
  const isSeat = currentUser?.role === "assistant";

  // Quotes view sets `window.__magGoToLead` and dispatches navigation here.
  // Pick the requested lead and clear the flag so subsequent visits start fresh.
  useEffect(() => {
    const targetId = typeof window !== 'undefined' ? window.__magGoToLead : null;
    if (!targetId) return;
    const exists = leads.some((l) => l.id === targetId);
    if (exists) {
      setSelectedId(targetId);
      setStatusFilter('all');
      setSearch('');
    }
    window.__magGoToLead = null;
  }, [leads]);

  // Sidebar click on "Leads" returns us to the list even with a lead selected.
  useEffect(() => {
    const h = (e) => { if (e?.detail?.view === 'leads') { setSelectedId(null); setShowForm(false); setEditLead(null); } };
    window.addEventListener('deskte:nav-reset', h);
    return () => window.removeEventListener('deskte:nav-reset', h);
  }, []);

  const STATUSES = [
    { id: "all", label: lang === "es" ? "Todos" : "All", color: "#7aa3c0" },
    { id: "new", label: lang === "es" ? "Nuevo" : "New", color: "#00d4ff", bg: "rgba(0,212,255,.12)" },
    { id: "contacted", label: lang === "es" ? "Contactado" : "Contacted", color: "#a78bfa", bg: "rgba(167,139,250,.12)" },
    { id: "qualified", label: lang === "es" ? "Calificado" : "Qualified", color: "#00ff88", bg: "rgba(0,255,136,.12)" },
    // Propuesta existe en el servidor (LeadStatus.PROPOSAL) desde siempre y
    // faltaba aqui. Como `getStatusObj` cae a STATUSES[1] cuando no encuentra
    // el estado, un lead en Propuesta se pintaba "Nuevo" -- y sin pastilla
    // propia, solo salia bajo "Todos", con la etiqueta equivocada.
    { id: "proposal", label: lang === "es" ? "Propuesta" : "Proposal", color: "#ffb700", bg: "rgba(255,183,0,.12)" },
    { id: "converted", label: lang === "es" ? "Convertido" : "Converted", color: "#10b981", bg: "rgba(16,185,129,.12)" },
    { id: "lost", label: lang === "es" ? "Perdido" : "Lost", color: "#ef4444", bg: "rgba(239,68,68,.12)" },
  ];
  const getStatusObj = (s) => STATUSES.find(x => x.id === s) || STATUSES[1];

  const filtered = leads.filter(l => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return `${l.firstName} ${l.lastName} ${l.email} ${l.phone} ${l.notes}`.toLowerCase().includes(q);
    }
    return true;
  });

  const selected = selectedId ? leads.find(l => l.id === selectedId) : null;

  // Reusable "Assigned to" suggestions: every distinct name already typed on a
  // lead (mirrors the Policies "Agent" combobox). Grows as you type new names.
  const assignOptions = [...new Set(leads.map(l => l.assignedLabel).filter(Boolean))].sort();

  const isApiModeLV = typeof window !== "undefined" && !!window.__deskteAuth;
  const updateLead = async (id, changes) => {
    if (isApiModeLV) {
      try { await apiUpdateLead(id, { ...(leads.find(l => l.id === id) || {}), ...changes }); }
      catch (err) { toast.error((lang === "es" ? "No se pudo actualizar el lead: " : "Failed to update lead: ") + err.message); return; }
    }
    onLeadsChange(prev => prev.map(l => l.id === id ? { ...l, ...changes } : l));
  };
  const deleteLead = async (id) => {
    if (isApiModeLV) {
      try { await apiDeleteLead(id); }
      catch (err) { toast.error((lang === "es" ? "No se pudo eliminar el lead: " : "Failed to delete lead: ") + err.message); return; }
    }
    onLeadsChange(prev => prev.filter(l => l.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const convertToClient = async (lead) => {
    if (isApiModeLV) {
      try {
        const { client, policiesCreated, policies } = await apiConvertLead(lead.id);
        // Map backend client → local shape used by Clients view.
        const mapped = {
          id: client.id,
          firstName: client.firstName,
          lastName: client.lastName,
          email: client.email || '',
          phone: client.phone || '',
          status: (client.status || 'ACTIVE').toLowerCase(),
          tags: client.tags || [],
          source: client.source || '',
          createdAt: client.createdAt,
          addressStreet: '', addressCity: '', addressState: '', addressZip: '',
        };
        // Dedup against the SSE `client.created` echo (which also adds by id).
        // Without this guard the converted client renders twice until refresh.
        onClientsChange(prev => prev.some(c => c.id === mapped.id)
          ? prev.map(c => c.id === mapped.id ? { ...c, ...mapped } : c)
          : [...prev, mapped]);
        onLeadsChange(prev => prev.map(l => l.id === lead.id ? { ...l, status: 'converted' } : l));
        // Push freshly auto-created policies into local state so they show up
        // immediately on the new client's Policies tab without needing a reload.
        // Map raw backend rows to the local shape (same as the SSE handler) and
        // let onPoliciesAdd dedup by id against the `policy.created` echo.
        if (onPoliciesAdd && policies.length > 0) {
          onPoliciesAdd(policies.map(mapPolicyFromApi));
        }
        const msg = policiesCreated > 0
          ? (lang === 'es'
              ? `Prospecto creado y ${policiesCreated} póliza(s) creada(s) automáticamente desde el quote aceptado. Míralo en "Prospecto y Referido".`
              : `Prospect created and ${policiesCreated} policy(ies) auto-created from the accepted quote. Find it in "Prospects & Referrals".`)
          : (lang === 'es' ? 'Prospecto creado. Míralo en "Prospecto y Referido".' : 'Prospect created. Find it in "Prospects & Referrals".');
        toast.success(msg);
      } catch (err) {
        toast.error((lang === 'es' ? 'No se pudo convertir: ' : 'Convert failed: ') + err.message);
      }
      return;
    }
    // Local fallback (demo mode without backend)
    const newClient = {
      id: "c_" + Date.now(),
      firstName: lead.firstName,
      lastName: lead.lastName,
      phone: lead.phone,
      email: lead.email,
      status: (lead.source && /refer/i.test(lead.source)) ? "referral" : "prospect",
      preferredLang: lead.lang === "spanish" ? "spanish" : "english",
      notes: `${lang === "es" ? "Convertido desde Lead" : "Converted from Lead"}. ${lead.notes || ""}`,
      createdAt: todayLocal(),
      addressStreet: "", addressCity: "", addressState: "", addressZip: "",
    };
    onClientsChange(prev => [...prev, newClient]);
    updateLead(lead.id, { status: "converted" });
  };

  const emptyLead = { id: "", firstName: "", lastName: "", phone: "", email: "", source: "referral", status: "new", notes: "", assignedTo: "Agente", assignedLabel: "", createdAt: todayLocal(), followUp: "", policyInterest: [], lang: "spanish" };

  const isApiModeL = typeof window !== "undefined" && !!window.__deskteAuth;
  const saveLead = async (form) => {
    if (editLead) {
      // Persist edits to the backend — without this the form's changes
      // (name, phone, email, source, follow-up, interests) only lived in
      // local state and were lost on reload/SSE refetch. Mirrors updateLead.
      if (isApiModeL) {
        try { await apiUpdateLead(editLead.id, { ...editLead, ...form }); }
        catch (err) { toast.error((lang === "es" ? "No se pudo actualizar el lead: " : "Failed to update lead: ") + err.message); return; }
      }
      onLeadsChange(prev => prev.map(l => l.id === editLead.id ? { ...editLead, ...form } : l));
    } else {
      if (isApiModeL) {
        try {
          const created = await apiCreateLead(form);
          // Merge-or-push: the SSE 'lead.created' echo may have already added
          // this row (by backend id) before the POST resolved, so dedup here
          // to avoid a double entry (feedback_sse_echo_dedup_optimistic_add).
          onLeadsChange(prev => prev.some(l => l.id === created.id)
            ? prev.map(l => l.id === created.id ? { ...l, ...form, ...created } : l)
            : [...prev, { ...form, ...created }]);
        } catch (err) {
          toast.error("Failed to save lead: " + err.message);
          return;
        }
      } else {
        onLeadsChange(prev => [...prev, { ...form, id: "l_" + Date.now() }]);
      }
    }
    setShowForm(false);
    setEditLead(null);
  };

  // LEAD FORM MODAL
  // LeadForm is defined at module scope (top of this file) so it keeps a stable
  // identity across re-renders — see the comment on that definition.

  return (
    <div>
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          <span style={{ fontSize: "1.4rem" }}>🎯</span>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--c-white,#eef6ff)", margin: 0 }}>Leads</h2>
          <span style={{ fontSize: ".75rem", color: "var(--c-text3,#45688a)", background: "var(--c-surface2,#112538)", padding: "2px 10px", borderRadius: 100, fontWeight: 700, border: "1px solid var(--c-border,#1a3550)" }}>{leads.length}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          {!isSeat && (
            <button type="button" onClick={() => setShowAutoReply(true)}
              title={lang === "es" ? "Auto-respuesta por correo a leads nuevos" : "Email auto-reply to new leads"}
              style={{ display: "flex", alignItems: "center", gap: 5, background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text2,#7aa3c0)", fontSize: ".78rem", fontWeight: 700, padding: ".5rem .85rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
              <Mail size={14} />{lang === "es" ? "Auto-respuesta" : "Auto-reply"}
            </button>
          )}
          <button type="button" onClick={() => { setEditLead(null); setShowForm(true); }}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
            <Plus size={14} />{lang === "es" ? "Nuevo Lead" : "New Lead"}
          </button>
        </div>
      </div>

      {/* STATUS FILTERS */}
      <div style={{ display: "flex", gap: ".35rem", marginBottom: ".75rem", flexWrap: "wrap" }}>
        {STATUSES.map(s => (
          <button type="button" key={s.id} onClick={() => setStatusFilter(s.id)}
            style={{ padding: ".3rem .7rem", borderRadius: 100, border: `1px solid ${statusFilter === s.id ? s.color + "55" : "var(--c-border,#1a3550)"}`, background: statusFilter === s.id ? (s.bg || "var(--c-surface2,#112538)") : "transparent", color: statusFilter === s.id ? s.color : "var(--c-text3,#45688a)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>
            {s.label} {s.id !== "all" && <span style={{ opacity: .7 }}>({leads.filter(l => l.status === s.id).length})</span>}
          </button>
        ))}
      </div>

      {/* SEARCH */}
      <div style={{ position: "relative", marginBottom: ".75rem" }}>
        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--c-text3,#45688a)" }} />
        <input className="dkt-inp" placeholder={lang === "es" ? "Buscar leads..." : "Search leads..."} value={search} onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 32, width: "100%", boxSizing: "border-box" }} />
      </div>

      {/* SPLIT PANEL */}
      <div style={{ display: "grid", gridTemplateColumns: selected ? "340px 1fr" : "1fr", gap: "1rem", alignItems: "start" }}>
        {/* LEFT - LIST */}
        <div style={{ display: "flex", flexDirection: "column", gap: ".4rem", maxHeight: "70vh", overflowY: "auto" }}>
          {filtered.length === 0 && <div style={{ textAlign: "center", padding: "2rem", color: "var(--c-text3,#45688a)", fontSize: ".8rem" }}>{lang === "es" ? "No se encontraron leads" : "No leads found"}</div>}
          {filtered.map(lead => {
            const st = getStatusObj(lead.status);
            const isActive = selectedId === lead.id;
            return (
              <button type="button" key={lead.id} onClick={() => setSelectedId(lead.id)}
                style={{ display: "flex", flexDirection: "column", gap: 4, padding: ".7rem .85rem", borderRadius: 10, border: `1px solid ${isActive ? "rgba(0,212,255,.4)" : "var(--c-border,#1a3550)"}`, background: isActive ? "var(--c-cyan-dim)" : "var(--c-surface2,#112538)", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "inherit", transition: "all .15s" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                  <span style={{ fontWeight: 700, fontSize: ".82rem", color: "var(--c-white,#eef6ff)" }}>{lead.firstName} {lead.lastName}</span>
                  <span style={{ fontSize: ".6rem", padding: "2px 8px", borderRadius: 100, fontWeight: 700, background: st.bg, color: st.color, border: `1px solid ${st.color}33` }}>{st.label}</span>
                </div>
                {lead.phone && <div style={{ fontSize: ".7rem", color: "var(--c-text2,#7aa3c0)" }}>{lead.phone}</div>}
                {(lead.policyInterest || []).length > 0 && (
                  <div style={{ display: "flex", gap: ".25rem", flexWrap: "wrap", marginTop: 2 }}>
                    {lead.policyInterest.map(pid => {
                      const pt = POLICY_TYPES.find(p => p.id === pid);
                      return pt ? <span key={pid} style={{ fontSize: ".58rem", padding: "1px 6px", borderRadius: 5, background: pt.color + "18", color: pt.color, fontWeight: 600, border: `1px solid ${pt.color}33` }}>{pt.icon} {lang === "es" ? pt.labelEs : pt.label}</span> : null;
                    })}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* RIGHT - DETAIL */}
        {selected && (
          <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1.25rem", position: "sticky", top: 10 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: "1.05rem", color: "var(--c-white,#eef6ff)" }}>{selected.firstName} {selected.lastName}</div>
                <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>{selected.email || (lang === "es" ? "Sin correo" : "No email")}</div>
              </div>
              <div style={{ display: "flex", gap: ".35rem" }}>
                <button type="button" onClick={() => { setEditLead(selected); setShowForm(true); }}
                  style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", padding: "5px 8px", borderRadius: 7, lineHeight: 0 }}><Edit2 size={13} /></button>
                <button type="button" onClick={async () => { if (await confirmDialog({ message: lang === "es" ? "¿Eliminar este lead?" : "Delete this lead?", danger: true })) deleteLead(selected.id); }}
                  style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text3,#45688a)", cursor: "pointer", padding: "5px 8px", borderRadius: 7, lineHeight: 0 }}><Trash2 size={13} /></button>
              </div>
            </div>

            {/* Quick Status Change */}
            <div style={{ marginBottom: "1rem" }}>
              <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".4rem", fontFamily: "var(--font-mono)" }}>{lang === "es" ? "Estado" : "Status"}</div>
              <div style={{ display: "flex", gap: ".3rem", flexWrap: "wrap" }}>
                {STATUSES.filter(s => s.id !== "all").map(s => (
                  <button type="button" key={s.id} onClick={() => updateLead(selected.id, { status: s.id })}
                    style={{ padding: ".28rem .6rem", borderRadius: 7, border: `1px solid ${selected.status === s.id ? s.color : "var(--c-border,#1a3550)"}`, background: selected.status === s.id ? s.bg : "transparent", color: selected.status === s.id ? s.color : "var(--c-text3,#45688a)", fontSize: ".68rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Info Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem", marginBottom: "1rem" }}>
              {[
                [lang === "es" ? "Teléfono" : "Phone", selected.phone, "📞"],
                [lang === "es" ? "Fuente" : "Source", (selected.source || "").replace("_", " ").replace(/\b\w/g, c => c.toUpperCase()), "📍"],
                [lang === "es" ? "Idioma" : "Language", selected.lang === "spanish" ? "🇪🇸 Español" : "🇺🇸 English", "🌐"],
                [lang === "es" ? "Creado" : "Created", fmt(selected.createdAt), "📅"],
                [lang === "es" ? "Seguimiento" : "Follow Up", selected.followUp ? fmt(selected.followUp) : "--", "🔔"],
                [lang === "es" ? "Asignado" : "Assigned", selected.assignedLabel || selected.assignedTo || "--", "👤"],
              ].map(([label, value, icon], i) => (
                <div key={i} style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 8, padding: ".45rem .6rem" }}>
                  <div style={{ fontSize: ".55rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)", marginBottom: 2 }}>{icon} {label}</div>
                  <div style={{ fontSize: ".78rem", fontWeight: 600, color: "var(--c-text,#c8dff0)" }}>{value || "--"}</div>
                </div>
              ))}
            </div>

            {/* Policy Interest */}
            {(selected.policyInterest || []).length > 0 && (
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".4rem", fontFamily: "var(--font-mono)" }}>{lang === "es" ? "Interés en Pólizas" : "Policy Interest"}</div>
                <div style={{ display: "flex", gap: ".3rem", flexWrap: "wrap" }}>
                  {selected.policyInterest.map(pid => {
                    const pt = POLICY_TYPES.find(p => p.id === pid);
                    return pt ? <span key={pid} style={{ fontSize: ".68rem", padding: ".2rem .55rem", borderRadius: 7, background: pt.color + "18", color: pt.color, fontWeight: 600, border: `1px solid ${pt.color}33` }}>{pt.icon} {lang === "es" ? pt.labelEs : pt.label}</span> : null;
                  })}
                </div>
              </div>
            )}

            {/* Notes */}
            {selected.notes && (
              <div style={{ marginBottom: "1rem" }}>
                <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".4rem", fontFamily: "var(--font-mono)" }}>{lang === "es" ? "Notas" : "Notes"}</div>
                <div style={{ fontSize: ".78rem", color: "var(--c-text,#c8dff0)", lineHeight: 1.5, background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 8, padding: ".55rem .7rem" }}><RichNote text={selected.notes} /></div>
              </div>
            )}

            {/* Convert to Prospect Button — lands in "Prospecto y Referido" */}
            {selected.status !== "converted" && (
              <button type="button" onClick={async () => { if (await confirmDialog(lang === "es" ? `¿Convertir a ${selected.firstName} ${selected.lastName} en prospecto? Pasará a "Prospecto y Referido".` : `Convert ${selected.firstName} ${selected.lastName} to a prospect? It moves to "Prospects & Referrals".`)) convertToClient(selected); }}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "linear-gradient(135deg,#059669,#10b981)", border: "none", color: "#fff", fontSize: ".82rem", fontWeight: 800, padding: ".65rem 1rem", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", letterSpacing: ".02em" }}>
                <CheckCircle size={16} />{lang === "es" ? "Convertir a Prospecto" : "Convert to Prospect"}
              </button>
            )}
            {selected.status === "converted" && (
              <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "rgba(16,185,129,.1)", border: "1px solid rgba(16,185,129,.3)", color: "#10b981", fontSize: ".82rem", fontWeight: 700, padding: ".65rem 1rem", borderRadius: 10 }}>
                <CheckCircle size={16} />{lang === "es" ? "Ya está en Prospecto y Referido" : "Now in Prospects & Referrals"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FORM MODAL */}
      {showForm && <LeadForm initial={editLead || emptyLead} onSave={saveLead} onCancel={() => { setShowForm(false); setEditLead(null); }}
        isEdit={!!editLead} lang={lang} assignOptions={assignOptions} emptyLead={emptyLead} />}

      {/* AUTO-REPLY SETTINGS MODAL */}
      {showAutoReply && <AutoReplySettings onClose={() => setShowAutoReply(false)} lang={lang} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   AUTO-REPLY SETTINGS — agency-wide email auto-reply to new leads
   ═══════════════════════════════════════════════════════════════ */
function AutoReplySettings({ onClose, lang }) {
  const es = lang === "es";
  const tx = (e, en) => (es ? e : en);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await getAgencySettings();
        if (cancelled) return;
        const cfg = s?.leadAutoReply || {};
        setEnabled(!!cfg.enabled);
        setSubject(cfg.subject || (es
          ? "¡Gracias por tu interés, {firstName}!"
          : "Thanks for reaching out, {firstName}!"));
        setBody(cfg.body || (es
          ? "Hola {firstName},\n\nGracias por contactarnos. Recibimos tu información sobre {interest} y te responderemos muy pronto.\n\nSaludos,\n{agentName}"
          : "Hi {firstName},\n\nThanks for reaching out. We received your info about {interest} and will get back to you shortly.\n\nBest,\n{agentName}"));
      } catch (err) {
        toast.error((es ? "No se pudo cargar: " : "Couldn't load: ") + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function save() {
    if (enabled && (!subject.trim() || !body.trim())) {
      toast.error(es ? "El asunto y el mensaje son obligatorios para activar." : "Subject and message are required to enable.");
      return;
    }
    setSaving(true);
    try {
      await updateAgencySettings({ leadAutoReply: { enabled, subject: subject.trim(), body: body.trim() } });
      toast.success(es ? "Auto-respuesta guardada" : "Auto-reply saved");
      onClose();
    } catch (err) {
      toast.error((es ? "No se pudo guardar: " : "Couldn't save: ") + err.message);
    } finally {
      setSaving(false);
    }
  }

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", display: "grid", placeItems: "center", zIndex: 1000, padding: "1rem" };
  const panel = { background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 16, padding: "1.5rem", maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto" };
  const inp = { width: "100%", padding: ".6rem .75rem", borderRadius: 9, background: "var(--c-surface2,#112538)", color: "var(--c-white,#eef6ff)", border: "1px solid var(--c-border,#1a3550)", fontSize: ".88rem", outline: "none", boxSizing: "border-box", fontFamily: "inherit" };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Mail size={18} color="#00d4ff" />
            <h3 style={{ margin: 0, fontSize: "1.1rem", color: "var(--c-white,#eef6ff)" }}>
              {tx("Auto-respuesta a leads nuevos", "Auto-reply to new leads")}
            </h3>
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer" }}><X size={18} /></button>
        </div>

        <p style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.55, marginTop: 0 }}>
          {tx("Cuando entra un lead nuevo (formulario, WhatsApp o creado a mano) se le envía este correo automáticamente desde tu correo conectado. Una sola vez por persona. No aplica a importaciones masivas.",
              "When a new lead arrives (form, WhatsApp or created by hand) this email is sent automatically from your connected mailbox. Once per person. Does not apply to bulk imports.")}
        </p>

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "var(--c-text2,#7aa3c0)" }}>{tx("Cargando…", "Loading…")}</div>
        ) : (
          <>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: ".75rem", background: "var(--c-surface2,#112538)", borderRadius: 10, border: "1px solid var(--c-border,#1a3550)", marginBottom: "1rem" }}>
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
              <div>
                <div style={{ fontSize: ".9rem", color: "var(--c-white,#eef6ff)", fontWeight: 700 }}>
                  {enabled ? tx("Activado", "Enabled") : tx("Desactivado", "Disabled")}
                </div>
                <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)" }}>
                  {tx("Mientras esté apagado, no se envía nada.", "While off, nothing is sent.")}
                </div>
              </div>
            </label>

            <div style={{ marginBottom: "1rem" }}>
              <div style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)", marginBottom: 4, fontWeight: 600 }}>{tx("Asunto", "Subject")}</div>
              <input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />
            </div>

            <div style={{ marginBottom: ".75rem" }}>
              <div style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)", marginBottom: 4, fontWeight: 600 }}>{tx("Mensaje", "Message")}</div>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={9} style={{ ...inp, resize: "vertical" }} />
            </div>

            <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", lineHeight: 1.6, marginBottom: "1.25rem", background: "var(--c-surface2,#112538)", padding: ".6rem .75rem", borderRadius: 8 }}>
              {tx("Etiquetas que puedes usar:", "Tags you can use:")}{" "}
              <code>{"{firstName}"}</code>, <code>{"{lastName}"}</code>, <code>{"{interest}"}</code>, <code>{"{agentName}"}</code>
              <div style={{ marginTop: 4 }}>
                {tx("Se agrega automáticamente tu dirección y el link de \"darse de baja\" (requisito legal CAN-SPAM).",
                    "Your address and an \"unsubscribe\" link are added automatically (CAN-SPAM legal requirement).")}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: ".5rem" }}>
              <button type="button" onClick={onClose} style={{ padding: ".6rem 1.1rem", borderRadius: 9, background: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", border: "1px solid var(--c-border,#1a3550)", cursor: "pointer", fontWeight: 700, fontSize: ".82rem" }}>
                {tx("Cancelar", "Cancel")}
              </button>
              <button type="button" onClick={save} disabled={saving} style={{ padding: ".6rem 1.25rem", borderRadius: 9, background: "linear-gradient(135deg,#00d4ff,#00a8c8)", color: "#0a1628", border: "none", cursor: "pointer", fontWeight: 800, fontSize: ".82rem", opacity: saving ? .6 : 1, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Save size={14} />{saving ? tx("Guardando…", "Saving…") : tx("Guardar", "Save")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const SEED_LEADS = [
  { id:"l1", firstName:"Jorge",   lastName:"Mendez",   phone:"305-555-0210", email:"jorge.mendez@example.com",   source:"referral",    status:"new",        notes:"Interesado en ACA y dental. Familia de 3.", assignedTo:"Agente", createdAt:"2025-12-01", followUp:"2026-01-10", policyInterest:["aca","dental"], lang:"spanish" },
  { id:"l2", firstName:"Patricia",lastName:"Flores",   phone:"813-555-0318", email:"pflores@example.com",      source:"facebook",    status:"contacted",  notes:"Vio anuncio en FB. Quiere cotización de auto.", assignedTo:"Agente", createdAt:"2025-12-05", followUp:"2026-01-12", policyInterest:["auto"], lang:"spanish" },
  { id:"l3", firstName:"Kevin",   lastName:"Thompson", phone:"407-555-0491", email:"kthompson@example.com",      source:"website",     status:"qualified",  notes:"Self-employed, needs health + life combo.", assignedTo:"Agente", createdAt:"2025-12-10", followUp:"2026-01-15", policyInterest:["aca","life"], lang:"english" },
  { id:"l4", firstName:"Maria",   lastName:"Santos",   phone:"305-555-0577", email:"msantos@example.com",        source:"referral",    status:"converted",  notes:"Convertida — cliente activa.", assignedTo:"Agente", createdAt:"2025-11-20", followUp:null, policyInterest:["aca"], lang:"spanish" },
  { id:"l5", firstName:"Ahmed",   lastName:"Hassan",   phone:"770-555-0622", email:"",                         source:"cold_call",   status:"lost",       notes:"No le interesó por el momento.", assignedTo:"Agente", createdAt:"2025-11-28", followUp:null, policyInterest:[], lang:"english" },
  { id:"l6", firstName:"Luz",     lastName:"Ramirez",  phone:"813-555-0733", email:"lramirez@example.com",       source:"event",       status:"new",        notes:"Conocida en feria de salud. Quiere medicare supplement.", assignedTo:"Agente", createdAt:"2025-12-18", followUp:"2026-01-20", policyInterest:["medicare","supp"], lang:"spanish" },
];

/* ═══════════════════════════════════════════════════════════════
   LANDING PAGE — Translations + Component
   ═══════════════════════════════════════════════════════════════ */
const LANDING_TX = {
  en: {
    badge: "ALL-IN-ONE CRM",
    headline: "The Only CRM That Works As Hard As You Do",
    subtitle: "Built by insurance agents, for insurance agents. Manage Health + P&C policies, commissions, compliance, and client retention — all in one bilingual platform.",
    ctaTrial: "Start Free 14-Day Trial",
    ctaDemo: "See Live Demo",
    statRecovered: "$6,200",
    statRecoveredLabel: "Avg Recovered / Year",
    statAccuracy: "92%",
    statAccuracyLabel: "Match Accuracy",
    statHours: "8 hrs",
    statHoursLabel: "Saved Per Month",
    navFeatures: "Features",
    navHow: "How It Works",
    navPricing: "Pricing",
    signIn: "Sign In",
    register: "Register",
    featuresTitle: "Everything You Need in One Desk",
    featuresSub: "18 powerful modules designed for the modern insurance agent",
    features: [
      { icon: "📋", title: "Smart Dashboard", desc: "Real-time overview of clients, policies, renewals and tasks" },
      { icon: "👥", title: "Client CRM", desc: "Full contact management with status tracking and notes" },
      { icon: "🏥", title: "Policy Manager", desc: "Health + P&C policies in one unified view" },
      { icon: "🔔", title: "Renewal Alerts", desc: "Never miss a renewal — automatic 30/60/90 day reminders" },
      { icon: "💰", title: "Commission Tracker", desc: "Track expected vs received, flag discrepancies automatically" },
      { icon: "📄", title: "Doc Requests", desc: "Request and track documents from clients digitally" },
      { icon: "✍️", title: "E-Signatures", desc: "SOA, consent forms, HIPAA — signed digitally in seconds" },
      { icon: "📁", title: "Smart Folders", desc: "Organize client files with color-coded folder system" },
      { icon: "📊", title: "Analytics", desc: "Conversion rates, revenue trends, agent performance" },
      { icon: "👨‍💼", title: "Team Management", desc: "Agency owner tools — assign roles, track agents, permissions" },
      { icon: "📥", title: "PDF Smart Import", desc: "AI-powered PDF parsing for policies and applications" },
      { icon: "🕐", title: "Time Tracking", desc: "Clock in/out with activity logging per agent" },
      { icon: "📆", title: "Calendar", desc: "Built-in scheduling with client appointment integration" },
      { icon: "🎯", title: "Lead Pipeline", desc: "Track prospects from first contact to converted client" },
      { icon: "💳", title: "Expense Tracker", desc: "Log business expenses and generate reports" },
      { icon: "🌐", title: "Bilingual EN/ES", desc: "Full interface available in English and Spanish" },
      { icon: "🔒", title: "Role-Based Access", desc: "Owner, Admin, Agent, Seat — each with proper permissions" },
      { icon: "📱", title: "Mobile Ready", desc: "Responsive design works on any device, anywhere" },
    ],
    howTitle: "How Commission Verification Works",
    howSub: "5 simple steps to never lose a dollar again",
    howSteps: [
      { step: "1", title: "Import Policies", desc: "Upload or enter your active policies with carrier details" },
      { step: "2", title: "Log Commissions", desc: "Record expected commissions per policy and payment schedule" },
      { step: "3", title: "Track Payments", desc: "Mark received payments as they arrive from carriers" },
      { step: "4", title: "Auto-Match", desc: "AI matches payments to policies and flags discrepancies" },
      { step: "5", title: "Recover Revenue", desc: "Get alerts on missing or short payments — recover what's yours" },
    ],
    policyTitle: "Health + P&C — All In One",
    policySub: "Stop juggling multiple systems. Deskte handles every policy type.",
    policyHealth: "Health Lines",
    policyPC: "Property & Casualty",
    healthTypes: ["ACA / Marketplace", "Medicare Advantage", "Medicare Supplement", "Medicare Part D", "Life Insurance", "Final Expense", "Dental", "Vision", "Accident", "Supplemental"],
    pcTypes: ["Homeowners", "Auto Insurance", "Renters", "Commercial", "Umbrella"],
    innovTitle: "What Makes Us Different",
    innovSub: "Innovation built for real insurance workflows",
    innovItems: [
      { icon: "🤖", title: "AI-Powered Import", desc: "Drop a PDF and our AI extracts policy data, client info, and premium details automatically." },
      { icon: "🔍", title: "Commission Forensics", desc: "We don't just track — we verify. Our system matches carrier payments against expected commissions." },
      { icon: "💵", title: "Up to 60% Less Than Competition", desc: "Starting at $45/mo vs $97-$300+ for competitors. Enterprise features at indie prices." },
      { icon: "🌎", title: "True Bilingual Platform", desc: "Not a translation plugin. Built from day one in English AND Spanish for the agents who need it." },
    ],
    pricingTitle: "Simple, Transparent Pricing",
    pricingSub: "No hidden fees. No per-policy charges. Cancel anytime.",
    plans: [
      {
        name: "SOLO",
        price: "$45",
        period: "/month",
        desc: "For solo agents getting started",
        features: ["1 Agent included", "Up to 1,000 clients", "500 E-Signatures/year", "500 Doc Requests/year", "Unlimited commissions", "Seat: +$9.99/mo", "Bilingual EN/ES", "PDF Smart Import"],
        cta: "Start Free Trial",
        popular: false,
      },
      {
        name: "AGENCY",
        price: "$200",
        period: "/month",
        desc: "For growing agencies",
        features: ["5 Agents included", "1,000 clients per agent", "4 Seats for the owner", "1,000 E-Signatures/year", "1,000 Doc Requests/year", "Extra agent: +$30/mo", "Agents' seats: +$9.99/mo each", "Leaderboard + Analytics"],
        cta: "Start Free Trial",
        popular: true,
        badge: "MOST POPULAR",
      },
      {
        name: "ENTERPRISE",
        price: "Custom",
        period: "",
        desc: "For large operations",
        features: ["Custom number of agents", "Custom client capacity", "Volume E-Signatures", "Volume Doc Requests", "API access + Integrations", "HIPAA compliance included", "Priority support", "Dedicated onboarding"],
        cta: "Contact Us",
        popular: false,
      },
    ],
    footerLine: "Deskte — Your Insurance Desk",
    footerCopy: "2026 DALCERSERVICES LLC. All rights reserved.",
  },
  es: {
    badge: "CRM TODO-EN-UNO",
    headline: "El Unico CRM que Trabaja tan Duro como Tu",
    subtitle: "Creado por agentes de seguros, para agentes de seguros. Administra polizas de Salud + P&C, comisiones, cumplimiento y retencion de clientes — todo en una plataforma bilingue.",
    ctaTrial: "Prueba Gratis 14 Dias",
    ctaDemo: "Ver Demo en Vivo",
    statRecovered: "$6,200",
    statRecoveredLabel: "Promedio Recuperado / Ano",
    statAccuracy: "92%",
    statAccuracyLabel: "Precision de Coincidencia",
    statHours: "8 hrs",
    statHoursLabel: "Ahorradas al Mes",
    navFeatures: "Funciones",
    navHow: "Como Funciona",
    navPricing: "Precios",
    signIn: "Iniciar Sesion",
    register: "Registrarse",
    featuresTitle: "Todo lo que Necesitas en Un Solo Escritorio",
    featuresSub: "18 modulos potentes disenados para el agente de seguros moderno",
    features: [
      { icon: "📋", title: "Dashboard Inteligente", desc: "Vision en tiempo real de clientes, polizas, renovaciones y tareas" },
      { icon: "👥", title: "CRM de Clientes", desc: "Gestion completa de contactos con seguimiento de estado y notas" },
      { icon: "🏥", title: "Gestor de Polizas", desc: "Polizas de Salud + P&C en una vista unificada" },
      { icon: "🔔", title: "Alertas de Renovacion", desc: "Nunca pierdas una renovacion — recordatorios automaticos 30/60/90 dias" },
      { icon: "💰", title: "Rastreador de Comisiones", desc: "Rastrea esperado vs recibido, senala discrepancias automaticamente" },
      { icon: "📄", title: "Solicitudes de Docs", desc: "Solicita y rastrea documentos de clientes digitalmente" },
      { icon: "✍️", title: "Firmas Digitales", desc: "SOA, formularios de consentimiento, HIPAA — firmados digitalmente en segundos" },
      { icon: "📁", title: "Carpetas Inteligentes", desc: "Organiza archivos de clientes con sistema de carpetas a color" },
      { icon: "📊", title: "Analiticas", desc: "Tasas de conversion, tendencias de ingresos, rendimiento de agentes" },
      { icon: "👨‍💼", title: "Gestion de Equipo", desc: "Herramientas para duenos de agencia — asignar roles, rastrear agentes" },
      { icon: "📥", title: "PDF Smart Import", desc: "Analisis de PDF con IA para polizas y solicitudes" },
      { icon: "🕐", title: "Control de Tiempo", desc: "Entrada/salida con registro de actividad por agente" },
      { icon: "📆", title: "Calendario", desc: "Programacion integrada con citas de clientes" },
      { icon: "🎯", title: "Pipeline de Leads", desc: "Rastrea prospectos desde el primer contacto hasta cliente convertido" },
      { icon: "💳", title: "Rastreador de Gastos", desc: "Registra gastos del negocio y genera reportes" },
      { icon: "🌐", title: "Bilingue EN/ES", desc: "Interfaz completa disponible en ingles y espanol" },
      { icon: "🔒", title: "Acceso por Roles", desc: "Dueno, Admin, Agente, Asiento — cada uno con permisos apropiados" },
      { icon: "📱", title: "Listo para Movil", desc: "Diseno responsivo funciona en cualquier dispositivo" },
    ],
    howTitle: "Como Funciona la Verificacion de Comisiones",
    howSub: "5 pasos simples para nunca perder un dolar",
    howSteps: [
      { step: "1", title: "Importar Polizas", desc: "Sube o ingresa tus polizas activas con detalles del carrier" },
      { step: "2", title: "Registrar Comisiones", desc: "Registra comisiones esperadas por poliza y calendario de pago" },
      { step: "3", title: "Rastrear Pagos", desc: "Marca pagos recibidos conforme llegan de los carriers" },
      { step: "4", title: "Auto-Coincidencia", desc: "La IA coincide pagos con polizas y senala discrepancias" },
      { step: "5", title: "Recuperar Ingresos", desc: "Recibe alertas sobre pagos faltantes o cortos — recupera lo tuyo" },
    ],
    policyTitle: "Salud + P&C — Todo en Uno",
    policySub: "Deja de hacer malabarismo con multiples sistemas. Deskte maneja cada tipo de poliza.",
    policyHealth: "Lineas de Salud",
    policyPC: "Propiedad y Accidentes",
    healthTypes: ["ACA / Marketplace", "Medicare Advantage", "Medicare Suplementario", "Medicare Parte D", "Seguro de Vida", "Gastos Finales", "Dental", "Vision", "Accidentes", "Suplementario"],
    pcTypes: ["Propietarios", "Seguro de Auto", "Inquilinos", "Comercial", "Paraguas"],
    innovTitle: "Lo que Nos Hace Diferentes",
    innovSub: "Innovacion creada para flujos reales de seguros",
    innovItems: [
      { icon: "🤖", title: "Importacion con IA", desc: "Sube un PDF y nuestra IA extrae datos de polizas, info del cliente y detalles de primas automaticamente." },
      { icon: "🔍", title: "Forense de Comisiones", desc: "No solo rastreamos — verificamos. Nuestro sistema coincide pagos de carriers contra comisiones esperadas." },
      { icon: "💵", title: "Hasta 60% Menos que la Competencia", desc: "Desde $45/mes vs $97-$300+ de competidores. Funciones enterprise a precios independientes." },
      { icon: "🌎", title: "Plataforma Verdaderamente Bilingue", desc: "No es un plugin de traduccion. Construido desde el dia uno en ingles Y espanol para los agentes que lo necesitan." },
    ],
    pricingTitle: "Precios Simples y Transparentes",
    pricingSub: "Sin cargos ocultos. Sin cobros por poliza. Cancela cuando quieras.",
    plans: [
      {
        name: "SOLO",
        price: "$45",
        period: "/mes",
        desc: "Para agentes individuales comenzando",
        features: ["1 Agente incluido", "Hasta 1,000 clientes", "500 Firmas Digitales/año", "500 Solicitudes de Docs/año", "Comisiones ilimitadas", "Asiento: +$9.99/mes", "Bilingue EN/ES", "PDF Smart Import"],
        cta: "Comenzar Prueba Gratis",
        popular: false,
      },
      {
        name: "AGENCIA",
        price: "$200",
        period: "/mes",
        desc: "Para agencias en crecimiento",
        features: ["5 Agentes incluidos", "1,000 clientes por agente", "4 Asientos para la dueña", "1,000 Firmas Digitales/año", "1,000 Solicitudes de Docs/año", "Agente extra: +$30/mes", "Asientos de los agentes: +$9.99/mes c/u", "Leaderboard + Analytics"],
        cta: "Comenzar Prueba Gratis",
        popular: true,
        badge: "MAS POPULAR",
      },
      {
        name: "ENTERPRISE",
        price: "Custom",
        period: "",
        desc: "Para operaciones grandes",
        features: ["Cantidad de agentes a medida", "Capacidad de clientes a medida", "Firmas Digitales por volumen", "Solicitudes de Docs por volumen", "API + Integraciones", "HIPAA compliance incluido", "Soporte prioritario", "Onboarding dedicado"],
        cta: "Contáctenos",
        popular: false,
      },
    ],
    footerLine: "Deskte — Your Insurance Desk",
    footerCopy: "2026 DALCERSERVICES LLC. Todos los derechos reservados.",
  },
};
